import { COINS_DISABLED } from '../config/coinsFlag';
/**
 * PurchaseCoinsModal — Backend-driven regional monetization modal
 *
 * Source of truth: /api/monetization/packages
 * Fallback: monetizationConfig.js
 *
 * Currency rules:
 *   - Bankak / bank transfer = SDG only
 *   - Card / international = USD only
 *
 * Props:
 *   initialTab: 'packages' | 'subscriptions'
 *   requiredCoins: number (shows shortage messaging)
 *   source: string (analytics source)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Wallet, Coins, ChevronRight, ChevronLeft, Crown, Zap, Star,
  ArrowRight, ArrowLeft, Package, Loader2, Shield, Check, TrendingUp,
  AlertTriangle
} from 'lucide-react';
import {
  getLabel,
  formatPrice,
  getMonetizationVariant,
  normalizePackage,
  normalizePlan,
  getPackagesForRegion,
  getPlansForRegion,
  MONETIZATION_LABELS,
} from '../config/monetizationConfig';
import { getAvailablePaymentMethods } from '../utils/paymentRouting';
import {
  trackBuyCoinsOpened,
  trackPackageSelected,
  trackPaymentStarted,
  trackSubscriptionClicked,
} from '../utils/monetizationAnalytics';
import BankakPaymentFlow from './BankakPaymentFlow';
import CardPaymentFlow from './CardPaymentFlow';
import './PurchaseCoinsModal.css';

// ─── Error Boundary ──────────────────────────────────
class PaymentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('PaymentErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      const isAr = this.props.lang?.startsWith('ar');
      return (
        <div style={{
          padding: 32, textAlign: 'center', direction: isAr ? 'rtl' : 'ltr',
        }} data-testid="payment-error-boundary">
          <AlertTriangle size={36} style={{ color: '#F59E0B', margin: '0 auto 12px' }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1E293B', marginBottom: 8 }}>
            {isAr ? 'حدث خطأ غير متوقع في خطوة الدفع.' : 'An unexpected error occurred in the payment step.'}
          </h3>
          <p style={{ fontSize: 13, color: '#64748B', marginBottom: 20 }}>
            {isAr ? 'يرجى المحاولة مرة أخرى.' : 'Please try again.'}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false });
              this.props.onClose?.();
            }}
            style={{
              padding: '10px 24px', borderRadius: 12, border: 'none',
              background: '#1E293B', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
            data-testid="payment-error-close"
          >
            {isAr ? 'إغلاق' : 'Close'}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const API = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseCoinsModal({
  open,
  onClose,
  onSuccess,
  initialTab = 'packages',
  suggestedPackageId = null,
  requiredCoins = 0,
  source = 'wallet',
}) {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');
  const lang = i18n.language || 'en';

  const [balance, setBalance] = useState(0);
  const [monthlySpend, setMonthlySpend] = useState(0);
  const [region, setRegion] = useState('SD');
  const [userId, setUserId] = useState('');
  const [activeSub, setActiveSub] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [step, setStep] = useState('select');
  const [bankakData, setBankakData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Backend-driven data
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);

  const getToken = () => localStorage.getItem('token');
  const variant = getMonetizationVariant(userId);

  // Fetch backend monetization config
  useEffect(() => {
    if (!open) return;
    trackBuyCoinsOpened(source);
    setActiveTab(initialTab);

    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${getToken()}` };
        const res = await fetch(`${API}/api/monetization/packages`, { headers });
        const data = await res.json();

        setBalance(data.current_balance || 0);
        setMonthlySpend(data.monthly_spend || 0);
        setRegion(data.region || 'SD');
        setActiveSub(data.active_subscription || null);

        // Normalize backend data
        const pkgs = (data.coinPackages || []).map(normalizePackage);
        const pls = (data.subscriptionPlans || []).map(normalizePlan);
        setPackages(pkgs.length > 0 ? pkgs : getPackagesForRegion('SD'));
        setPlans(pls.length > 0 ? pls : getPlansForRegion('SD'));

        // Get userId
        const userRes = await fetch(`${API}/api/auth/me`, { headers });
        const userData = await userRes.json();
        setUserId(userData.user_id || '');
      } catch (e) {
        console.error('Failed to fetch monetization data:', e);
        // Use fallback
        setPackages(getPackagesForRegion('SD'));
        setPlans(getPlansForRegion('SD'));
      }
    };
    fetchData();
  }, [open, source, initialTab]);

  // Pre-select suggested package
  useEffect(() => {
    if (requiredCoins > 0 && packages.length > 0) {
      const covering = packages.filter(p => p.coins >= requiredCoins).sort((a, b) => a.coins - b.coins);
      const best = covering[0] || packages[packages.length - 1];
      if (best) setSelectedPkg(best);
    } else if (suggestedPackageId && packages.length > 0) {
      const pkg = packages.find(p => p.id === suggestedPackageId);
      if (pkg) setSelectedPkg(pkg);
    }
  }, [suggestedPackageId, requiredCoins, packages]);

  const paymentMethods = getAvailablePaymentMethods(region === 'SD' ? 'SD' : 'US');

  const handleContinue = () => {
    const sel = activeTab === 'packages' ? selectedPkg : selectedPlan;
    if (!sel) return;
    if (activeTab === 'subscriptions' && selectedPlan?.id) trackSubscriptionClicked(selectedPlan.id);
    if (paymentMethods.length === 1) {
      handlePaymentMethodSelect(paymentMethods[0].id);
    } else {
      setStep('payment_method');
    }
  };

  const handlePaymentMethodSelect = async (method) => {
    const sel = activeTab === 'packages' ? selectedPkg : selectedPlan;
    if (!sel) return;
    const targetType = activeTab === 'packages' ? 'package' : 'subscription';
    const targetId = sel.id || '';
    trackPaymentStarted(method, targetId);

    if (method === 'bankak') {
      await createBankakRequest(targetType, targetId);
    } else {
      await createCheckoutSession(targetType, targetId);
    }
  };

  const createBankakRequest = async (targetType, targetId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/monetization/bankak/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ target_type: targetType, target_id: targetId }),
      });
      const data = await res.json();
      if (data.success) {
        setBankakData({
          ...data,
          target_type: targetType,
          target_id: targetId,
          coins: activeTab === 'packages' ? (selectedPkg?.coins || 0) : 0,
        });
        setStep('bankak');
      }
    } catch (e) {
      console.error('Bankak create failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const createCheckoutSession = async (targetType, targetId) => {
    setLoading(true);
    try {
      // Card = USD only. Look up USD pricing from DEFAULT region.
      const sel = activeTab === 'packages' ? selectedPkg : selectedPlan;
      const usdPackages = getPackagesForRegion('DEFAULT');
      const usdPlans = getPlansForRegion('DEFAULT');

      let usdAmount = 0;
      if (activeTab === 'packages') {
        // Find matching USD package by coins count
        const usdPkg = usdPackages.find(p => p.coins === (sel?.coins || 0)) || usdPackages[0];
        usdAmount = usdPkg?.price || sel?.price || 0;
      } else {
        // Find matching USD plan by id
        const usdPlan = usdPlans.find(p => p.id === (sel?.id || '')) || usdPlans[0];
        usdAmount = usdPlan?.price || sel?.price || 0;
      }

      setBankakData({
        target_type: targetType,
        target_id: targetId,
        coins: activeTab === 'packages' ? (sel?.coins || 0) : 0,
        amount: usdAmount,
        currency: 'USD',
      });
      setStep('card_payment');
    } catch (e) {
      console.error('Card payment setup failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleBankakSuccess = () => {
    setStep('success');
    onSuccess?.();
  };

  const handleClose = useCallback(() => {
    setStep('select');
    setSelectedPkg(null);
    setSelectedPlan(null);
    setBankakData(null);
    onClose();
  }, [onClose]);

  // One-step back navigation that mirrors the forward flow:
  //   select  ←  payment_method  ←  bankak / card_payment
  // Header Back button is hidden on the `select` step (X closes the modal).
  const handleHeaderBack = useCallback(() => {
    if (step === 'bankak' || step === 'card_payment') {
      setBankakData(null);
      setStep('payment_method');
    } else if (step === 'payment_method') {
      setStep('select');
    } else if (step === 'success') {
      // Treat back-from-success as close (purchase already submitted)
      handleClose();
    }
  }, [step, handleClose]);

  if (COINS_DISABLED) return null;
  if (!open) return null;

  const highlightKey = variant === 'B' ? 'popular' : 'best_value';

  return (
    <div className="purchase-overlay" onClick={() => step === 'select' && handleClose()} dir={isAr ? 'rtl' : 'ltr'} data-testid="purchase-coins-modal">
      <div className="purchase-sheet" onClick={(e) => e.stopPropagation()}>
        <PaymentErrorBoundary lang={lang} onClose={handleClose}>

        {/* HEADER */}
        <div className="purchase-header">
          {step !== 'select' && (
            <button
              className="purchase-back"
              onClick={handleHeaderBack}
              data-testid="purchase-back"
              aria-label={isAr ? 'رجوع' : 'Back'}
              style={{
                position: 'absolute',
                top: 12,
                insetInlineStart: 12,
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: 'none',
                borderRadius: 999,
                width: 32,
                height: 32,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 2,
              }}
            >
              {isAr ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
            </button>
          )}
          <button className="purchase-close" onClick={handleClose} data-testid="purchase-close"><X size={16} /></button>
          <div className="purchase-header-content">
            <div className="purchase-header-icon"><Coins size={24} /></div>
            <h2 className="purchase-title">{getLabel('buy_coins', lang)}</h2>
            <div className="purchase-balance-pill" data-testid="purchase-balance">
              <Wallet size={13} />
              <span>{isAr ? 'رصيدك:' : 'Balance:'}</span>
              <strong>{balance}</strong>
              <span>{isAr ? 'عملة' : 'coins'}</span>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="purchase-body">

          {step === 'select' && (
            <>
              {/* Shortage message */}
              {requiredCoins > 0 && requiredCoins > balance && (
                <div className="purchase-shortage" data-testid="purchase-shortage">
                  <Zap size={14} />
                  <span>
                    {isAr
                      ? `تحتاج ${requiredCoins - balance} عملة إضافية فقط`
                      : `You only need ${requiredCoins - balance} more coins`
                    }
                  </span>
                </div>
              )}

              {/* Monthly spend insight */}
              {monthlySpend > 0 && !activeSub && (
                <div className="purchase-spend-insight" data-testid="monthly-spend-insight" style={{
                  padding: '8px 12px', margin: '0 0 10px', borderRadius: 10,
                  background: monthlySpend >= 15 ? '#FFF7ED' : '#F0FDF4',
                  border: `1px solid ${monthlySpend >= 15 ? '#FDBA74' : '#BBF7D0'}`,
                  fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <TrendingUp size={13} style={{ color: monthlySpend >= 15 ? '#EA580C' : '#16A34A' }} />
                  <span style={{ color: '#374151' }}>
                    {isAr
                      ? `أنفقت ${monthlySpend} عملة هذا الشهر`
                      : `You spent ${monthlySpend} coins this month`
                    }
                    {monthlySpend >= 15 && (
                      <strong style={{ color: '#EA580C', marginInlineStart: 4 }}>
                        {isAr ? '— وفّر مع برو!' : '— Save with Pro!'}
                      </strong>
                    )}
                  </span>
                </div>
              )}

              {/* Tabs */}
              <div className="purchase-tabs" data-testid="purchase-tabs">
                <button className={`purchase-tab ${activeTab === 'packages' ? 'active' : ''}`} onClick={() => setActiveTab('packages')} data-testid="tab-packages">
                  <Package size={14} /> {isAr ? 'باقات العملات' : 'Coin Packs'}
                </button>
                <button className={`purchase-tab ${activeTab === 'subscriptions' ? 'active' : ''}`} onClick={() => setActiveTab('subscriptions')} data-testid="tab-subscriptions">
                  <Crown size={14} /> {isAr ? 'الاشتراكات' : 'Subscriptions'}
                </button>
              </div>

              {/* Packages Grid */}
              {activeTab === 'packages' && (
                <div className="purchase-grid" data-testid="packages-grid">
                  {packages.map((pkg) => {
                    const isSelected = selectedPkg?.id === pkg.id;
                    const isHighlighted = pkg.labelKey === highlightKey || pkg.recommended;
                    const label = getLabel(pkg.labelKey, lang);
                    return (
                      <button
                        key={pkg.id}
                        className={`pkg-card ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                        onClick={() => { setSelectedPkg(pkg); trackPackageSelected(pkg.id, region); }}
                        data-testid={`pkg-${pkg.id}`}
                      >
                        {isHighlighted && <span className="pkg-badge">{isAr ? (pkg.badge_ar || getLabel(pkg.badge || pkg.labelKey, lang)) : (pkg.badge_en || getLabel(pkg.badge || pkg.labelKey, lang))}</span>}
                        <div className="pkg-coins">
                          <Coins size={20} style={{ color: '#F97316' }} />
                          <span className="pkg-amount">{pkg.coins}</span>
                        </div>
                        <div className="pkg-name">{label}</div>
                        <div className="pkg-price">{formatPrice(pkg.price, pkg.currency, lang)}</div>
                        {isSelected && <div className="pkg-check"><Check size={12} color="white" /></div>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Subscriptions List */}
              {activeTab === 'subscriptions' && (
                <div className="plans-list" data-testid="plans-list">
                  {activeSub && (
                    <div style={{ padding: '8px 12px', marginBottom: 10, borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Check size={14} style={{ color: '#16A34A' }} />
                      <span style={{ color: '#166534' }}>
                        {isAr ? `أنت مشترك في ${getLabel(activeSub.plan_id, lang)}` : `You're subscribed to ${getLabel(activeSub.plan_id, lang)}`}
                      </span>
                    </div>
                  )}
                  {plans.map((plan, idx) => {
                    const isSelected = selectedPlan?.id === plan.id;
                    const isPro = plan.id === 'pro';
                    return (
                      <button
                        key={plan.id}
                        className={`plan-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedPlan(plan)}
                        data-testid={`plan-${plan.id}`}
                        style={isPro ? { border: '2px solid #F97316' } : undefined}
                      >
                        {isPro && (
                          <span style={{
                            position: 'absolute', top: -8, right: isAr ? 'auto' : 12, left: isAr ? 12 : 'auto',
                            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                            background: '#F97316', color: 'white',
                          }}>
                            {isAr ? 'الأكثر شيوعاً' : 'Most Popular'}
                          </span>
                        )}
                        <div className="plan-header-row">
                          <div className="plan-name">
                            <Crown size={16} style={{ color: isPro ? '#F97316' : '#8B5CF6' }} />
                            {getLabel(plan.id, lang)}
                          </div>
                          <div className="plan-price">
                            {formatPrice(plan.price, plan.currency, lang)}{getLabel('per_month', lang)}
                          </div>
                        </div>
                        <div className="plan-benefits">
                          {plan.coinsPerMonth > 0 && (
                            <span className="plan-benefit"><Coins size={12} /> {plan.coinsPerMonth} {getLabel('coins_per_month', lang)}</span>
                          )}
                          {plan.freeBoostsPerWeek > 0 && (
                            <span className="plan-benefit"><Zap size={12} /> {plan.freeBoostsPerWeek} {isAr ? 'تعزيز مجاني/أسبوع' : 'free boosts/week'}</span>
                          )}
                          {plan.features.map(f => (
                            <span key={f} className="plan-benefit"><Star size={10} /> {getLabel(f, lang)}</span>
                          ))}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Trust */}
              <div className="purchase-trust">
                <Shield size={14} />
                <span>{isAr ? 'دفع آمن — عملاتك تُضاف فوراً بعد التحقق' : 'Secure payment — coins credited after verification'}</span>
              </div>
            </>
          )}

          {step === 'payment_method' && (
            <div className="payment-methods" data-testid="payment-methods">
              <div className="pm-label">{isAr ? 'اختر طريقة الدفع' : 'Select payment method'}</div>
              {paymentMethods.map(pm => (
                <button key={pm.id} className="pm-option" onClick={() => handlePaymentMethodSelect(pm.id)} disabled={loading} data-testid={`pm-${pm.id}`}
                  style={{ direction: isAr ? 'rtl' : 'ltr' }}
                >
                  <div className="pm-icon" style={{ background: pm.id === 'bankak' ? '#EDE9FE' : '#DBEAFE' }}>
                    <span style={{ fontSize: 20 }}>{pm.id === 'bankak' ? '🏦' : '💳'}</span>
                  </div>
                  <div className="pm-info" style={{ textAlign: isAr ? 'right' : 'left' }}>
                    <div className="pm-name">{getLabel(pm.labelKey, lang)}</div>
                    <div className="pm-desc">{pm.id === 'bankak' ? (isAr ? 'تحويل بنكي — السودان' : 'Bank transfer — Sudan') : (isAr ? 'بطاقة فيزا / تحويل دولي' : 'Visa Card / International')}</div>
                  </div>
                  {loading ? <Loader2 size={18} className="animate-spin" style={{ color: '#9CA3AF' }} /> : (isAr ? <ChevronLeft size={18} style={{ color: '#9CA3AF' }} /> : <ChevronRight size={18} style={{ color: '#9CA3AF' }} />)}
                </button>
              ))}
              <button className="pm-back" onClick={() => setStep('select')} data-testid="pm-back">
                {isAr ? <ArrowRight size={14} /> : <ArrowLeft size={14} />}
                {isAr ? 'رجوع' : 'Back'}
              </button>
            </div>
          )}

          {step === 'bankak' && bankakData && (
            <BankakPaymentFlow data={bankakData} lang={lang} onSuccess={handleBankakSuccess} onBack={() => setStep('payment_method')} />
          )}

          {step === 'card_payment' && (
            <CardPaymentFlow
              data={bankakData}
              lang={lang}
              onSuccess={handleBankakSuccess}
              onBack={() => setStep('payment_method')}
            />
          )}

          {step === 'success' && (
            <div className="purchase-success" data-testid="purchase-success">
              <div className="success-icon"><Zap size={32} color="#16A34A" /></div>
              <h3>{isAr ? 'تم بنجاح!' : 'Submitted!'}</h3>
              <p>{getLabel('waiting_confirmation', lang)}</p>
              <button className="success-done-btn" onClick={handleClose} data-testid="purchase-done">{isAr ? 'تم' : 'Done'}</button>
            </div>
          )}
        </div>

        {/* FOOTER CTA */}
        {step === 'select' && (
          <div className="purchase-footer">
            <button className="purchase-cta" onClick={handleContinue} disabled={activeTab === 'packages' ? !selectedPkg : !selectedPlan} data-testid="purchase-continue">
              <span>{isAr ? 'متابعة' : 'Continue'}</span>
              {isAr ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
            </button>
          </div>
        )}
        </PaymentErrorBoundary>
      </div>
    </div>
  );
}
